// src/services/chatStartDm.js
//
// Chat v2: start a 1:1 DM with another user, or return the existing
// chat id if one already exists. Idempotent — calling twice with
// the same pair returns the same chat id.
//
// We do an "exists" check client-side first because Firestore rules
// (Phase 1) only allow create with participantIds.size() == 2. If
// we tried to create a duplicate, the rule would still reject it
// only if there were a uniqueness constraint — which Firestore doesn't
// have natively. So this client-side check is the only thing keeping
// pairs from accumulating multiple empty DM docs.
//
// Race condition: two clients calling startOrOpenDm at the same
// moment could both create a DM and end up with two. We accept
// that risk for MVP (probability is essentially zero with two
// users and a single tab each). A Cloud Function dedupe sweep is
// the long-term fix if it becomes a problem.

import {
  addDoc,
  collection,
  query,
  where,
  getDocs,
  serverTimestamp,
} from 'firebase/firestore'
import { db } from '@/firebase/firebase'

/**
 * Returns the chatId of an existing or newly created 1:1 DM
 * between `me` and `otherUid`.
 *
 * @param {Object} me  Current user info for denormalization
 * @param {string} me.uid
 * @param {string} me.displayName
 * @param {string|null} me.photoURL
 * @param {Object} other  Other user info
 * @param {string} other.uid
 * @param {string} other.displayName
 * @param {string|null} other.photoURL
 * @returns {Promise<string>}
 */
export async function startOrOpenDm({ me, other }) {
  if (!me?.uid) throw new Error('Missing current user uid')
  if (!other?.uid) throw new Error('Missing other user uid')
  if (me.uid === other.uid) throw new Error("Can't DM yourself")

  // 1. Check for an existing DM. We query DMs where I'm a
  // participant, then filter client-side for ones containing
  // the other user. (Firestore can't `array-contains` two
  // values in a single query.)
  const q = query(
    collection(db, 'chats'),
    where('type', '==', 'dm'),
    where('participantIds', 'array-contains', me.uid),
  )
  const snap = await getDocs(q)
  for (const docSnap of snap.docs) {
    const ids = docSnap.data().participantIds || []
    if (ids.length === 2 && ids.includes(other.uid)) {
      return docSnap.id
    }
  }

  // 2. Create a new DM. Display name = the OTHER person's name
  // for both participants (the inbox row needs to show "the
  // person I'm chatting with", not myself).
  // NOTE: this means each side of the DM sees a different `name`
  // on the chat doc... but actually we store just one name. The
  // inbox can derive the right display from participantInfo, OR
  // we can store both display names and pick at render. For MVP
  // simplicity, we store the other person's name. If you (me)
  // open the chat, you see "their name" as the title — which is
  // what you want. If they open it, they see "your name" — which
  // requires us to store BOTH names.
  //
  // Easiest fix: store both names, render side-aware. The chat
  // doc carries `members.{uid}.displayName` for both, so the
  // ChatRoomV2 header can pick the OTHER member's name dynamically.
  const newRef = await addDoc(collection(db, 'chats'), {
    type: 'dm',
    teamId: null,
    clubId: null,
    name: other.displayName, // fallback for inbox; ChatRoom renders dynamically
    photoUrl: other.photoURL || null,
    participantIds: [me.uid, other.uid],
    members: {
      [me.uid]: {
        role: 'admin',
        displayName: me.displayName || 'You',
        photoURL: me.photoURL || null,
        addedAt: serverTimestamp(),
        addedBy: me.uid,
      },
      [other.uid]: {
        role: 'admin',
        displayName: other.displayName || 'Member',
        photoURL: other.photoURL || null,
        addedAt: serverTimestamp(),
        addedBy: me.uid,
      },
    },
    createdAt: serverTimestamp(),
    createdBy: me.uid,
    updatedAt: serverTimestamp(),
    lastMessage: null,
    lastMessageAt: null,
  })
  return newRef.id
}
